import { useMemo, useEffect } from "react";

import { usePublicAppStore } from "@/store";
import { googleIdentityModule } from "@/modules/google-identity";
import { useLocation, useNavigate } from "react-router-dom";
import { localStorageModule } from "@/modules/local-storage";
import { appRoutes } from "@/app/router";
import { windowModule } from "@/modules/window";
import { logger } from "@/modules/logger";
import { toastModule } from "@/modules/toast";
import { readIMessageClaimFlowSession } from "@/modules/mem-agent/imessage-claim-flow-session";

const BARE_LANDING_RETRY_COUNT_KEY = "google-oauth-bare-landing-retry-count";
const MAX_BARE_LANDING_RETRIES = 2;

const readBareLandingRetryCount = (): number => {
  try {
    return Number(sessionStorage.getItem(BARE_LANDING_RETRY_COUNT_KEY)) || 0;
  } catch {
    // Without storage we can't bound retries, so treat them as exhausted.
    return MAX_BARE_LANDING_RETRIES;
  }
};

const writeBareLandingRetryCount = (count: number): void => {
  try {
    sessionStorage.setItem(BARE_LANDING_RETRY_COUNT_KEY, String(count));
  } catch {
    // Ignore; reads fall back to exhausted.
  }
};

const clearBareLandingRetryCount = (): void => {
  try {
    sessionStorage.removeItem(BARE_LANDING_RETRY_COUNT_KEY);
  } catch {
    // Ignore.
  }
};

export const useGoogleOAuthRedirectPageBehavior = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { publicStore } = usePublicAppStore();

  const { redirectURI, code, hasExplicitError } = useMemo(() => {
    const redirectURI = windowModule.getGoogleIdentityOAuthRedirectUrl();

    try {
      const { code } = googleIdentityModule.parseOAuthCode({ location });
      return { redirectURI, code, hasExplicitError: false };
    } catch (error) {
      logger.info({
        message: "Google OAuth failed or was cancelled",
        info: {
          error: error instanceof Error ? error.message : String(error),
          searchParams: location.search,
        },
      });

      return {
        redirectURI,
        code: null,
        hasExplicitError: new URLSearchParams(location.search).has("error"),
      };
    }
  }, [location]);

  // Handle OAuth errors by showing toast and navigating to login
  useEffect(() => {
    if (code) {
      clearBareLandingRetryCount();
      return;
    }

    /**
     * A landing with neither `code` nor `error` is a Google-side bounce
     * (observed on Chrome for iOS) where retrying the same flow usually
     * succeeds, so retry before falling back to the error toast.
     */
    if (!hasExplicitError) {
      const retryCount = readBareLandingRetryCount();

      if (retryCount < MAX_BARE_LANDING_RETRIES) {
        writeBareLandingRetryCount(retryCount + 1);
        logger.warn({
          message: "Google OAuth redirect landed without code or error. Retrying log-in.",
          info: { retryCount },
        });
        void googleIdentityModule.triggerLogIn();
        return;
      }

      clearBareLandingRetryCount();
    }

    toastModule.triggerToast({
      content: "Authentication was cancelled or failed. Please try again.",
    });
    navigate(appRoutes.logIn({}).path, { replace: true });
  }, [code, hasExplicitError, navigate]);

  useEffect(() => {
    // Don't attempt authentication if there was no code
    if (!code) {
      return;
    }

    /**
     * Revisiting this route via browser history re-submits a code Google has
     * already consumed, which fails the exchange. If we're already
     * authenticated, skip it and let the redirect effect below route onwards.
     */
    if (publicStore.auth.isAuthenticated) {
      return;
    }

    const asyncData = publicStore.auth.authenticateAccountUsingGoogleOAuthCodeState;

    /**
     * If we've already made a request, don't make another one.
     */
    if (!!asyncData.isLoading || !!asyncData.failedAt || !!asyncData.data) {
      return;
    }

    /**
     * Async.
     */
    publicStore.auth.authenticateAccountUsingGoogleOAuthCode({
      code,
      redirectURI,
      isSignUp: false,
      includeNativeChatOnboarding: !readIMessageClaimFlowSession(),
    });
  }, [code, publicStore.auth, redirectURI]);

  const state = publicStore.auth.authenticateAccountUsingGoogleOAuthCodeState;

  useEffect(() => {
    if (!publicStore.auth.isAuthenticated) {
      return;
    }

    const asyncLogic = async () => {
      const { pathname, search, hash } = await localStorageModule.readPendingRedirectPath();
      await localStorageModule.clearPendingRedirectPath();

      const defaultRedirectPath = appRoutes.notesList({}).path;
      const targetPathname = pathname ?? defaultRedirectPath;

      /**
       * Using this because we don't have access to the `navigation` store when
       * on a public route. Replace so the consumed-code URL doesn't remain in
       * history.
       */
      if (!pathname) {
        navigate(targetPathname, { replace: true });
        return;
      }

      navigate(
        {
          pathname: targetPathname,
          search: search ?? undefined,
          hash: hash ?? undefined,
        },
        { replace: true }
      );
    };

    asyncLogic();
  }, [navigate, publicStore.auth.isAuthenticated]);

  return state;
};
